import { useState, useMemo, useCallback, useRef } from 'react';
import { useRecordContext, useGetOne } from 'react-admin';
import { Typography, Box } from '@mui/material';
import type { Variant, VariantListItem, VariantChangeImpact } from '@src/types';
import type { PartnerSettings } from '@src/settings';
import VariantsList from './VariantsList';
import VariantDetailPanel from './VariantDetailPanel';
import type { VariantSaveRef } from './VariantDetailPanel';
import ConfirmVariantChangeDialog from './ConfirmVariantChangeDialog';

interface VariantsSectionProps {
  variantSaveRef?: VariantSaveRef;
}

const VariantsSection = ({ variantSaveRef }: VariantsSectionProps) => {
  const record = useRecordContext();
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const currencyCode = settings?.primaryCurrencyCode || 'USD';

  // --- Confirmation dialog state (owned here so it survives VariantDetailPanel remounts) ---
  const confirmSaveRef = useRef<(() => Promise<void>) | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isFetchingImpact, setIsFetchingImpact] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [impact, setImpact] = useState<VariantChangeImpact | null>(null);

  const onConfirmNeeded = useCallback((doSave: () => Promise<void>) => {
    confirmSaveRef.current = doSave;
    setIsConfirmOpen(true);
    setIsFetchingImpact(true);
    setImpact(null);
  }, []);

  const onImpactLoaded = useCallback((result: VariantChangeImpact | null) => {
    setImpact(result);
    setIsFetchingImpact(false);
  }, []);

  const handleConfirmClose = useCallback(() => {
    if (!isSaving) {
      setIsConfirmOpen(false);
      setImpact(null);
      confirmSaveRef.current = null;
    }
  }, [isSaving]);

  const handleConfirmSave = useCallback(async () => {
    if (!confirmSaveRef.current) return;
    setIsSaving(true);
    try {
      await confirmSaveRef.current();
      setIsConfirmOpen(false);
      setImpact(null);
      confirmSaveRef.current = null; // eslint-disable-line require-atomic-updates
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Flatten all variants into individual Color / Size entries
  const variantItems: VariantListItem[] = useMemo(() => {
    if (!record?.variants || !Array.isArray(record.variants)) return [];
    return record.variants.flatMap((variant: Variant) =>
      variant.skus.map((sku: string, i: number) => ({
        sku,
        colorDisplay: variant.colorDisplay,
        sizeDisplay: variant.sizeDisplays?.[i] ?? '',
      })));
  }, [record]);

  // Always render the dialog — its state lives here, not in VariantDetailPanel
  const confirmDialog = (
    <ConfirmVariantChangeDialog
      open={isConfirmOpen}
      onClose={handleConfirmClose}
      onConfirm={handleConfirmSave}
      isLoading={isFetchingImpact}
      isSaving={isSaving}
      impact={impact}
      currencyCode={currencyCode}
    />
  );

  if (!record?.variants || !Array.isArray(record.variants)) {
    return (
      <>
        {confirmDialog}
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No variants available
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      {confirmDialog}
      <Box sx={{ display: 'flex', width: '100%', minHeight: 500, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        {/* Left column - variant list */}
        <Box sx={{ width: 280, minWidth: 280, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
          <VariantsList
            items={variantItems}
            selectedSku={selectedSku}
            onSelect={setSelectedSku}
            productName={record.productName}
          />
        </Box>

        {/* Right column - variant detail editor */}
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          {selectedSku ? (
            <VariantDetailPanel
              key={selectedSku}
              sku={selectedSku}
              parentSku={record.id as string}
              variantSaveRef={variantSaveRef}
              onConfirmNeeded={onConfirmNeeded}
              onImpactLoaded={onImpactLoaded}
            />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Typography variant="body1" color="text.secondary">
                Select a variant from the list to view and edit its details
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default VariantsSection;
